/**
 * 自動コンポーネント初期化システム
 * data-component属性を持つ要素を自動的に検出し、対応するコンポーネントを動的に読み込み初期化
 */

/**
 * Viteのglobインポートでコンポーネントを遅延読み込み
 * これによりViteが静的解析可能になり、コード分割が正しく機能する
 * theme.jsで静的インポートされているコンポーネントは除外
 */
const componentModules =
  /** @type {Record<string, () => Promise<Record<string, unknown>>>} */ (
    import.meta.glob([
      '../components/*.js',
      '!../components/loader-index.js',
      '!../components/loader-page.js',
      '!../components/link-hover-color.js',
      '!../components/smooth-scroll.js',
    ])
  );

/**
 * theme.js で静的インポート + 初期化される component 名。
 * data-component 属性が markup にあっても ComponentLoader 側では skip し
 * "Unknown component" 警告を出さない (#570)。
 */
const STATIC_COMPONENTS = new Set(['loader-index']);

/**
 * コンポーネント名とファイル名・クラス名のマッピング
 * 新しいコンポーネント追加時はここに追加するだけでOK
 * ※ theme.jsで静的インポートするコンポーネントはここに含めない
 * @type {Record<string, {file: string, className: string}>}
 */
const COMPONENT_MAP = {
  'mobile-menu': { file: 'mobile-menu.js', className: 'MobileMenu' },
  'marquee-scroll': { file: 'marquee-scroll.js', className: 'MarqueeScroll' },
  'text-animation': { file: 'text-animation.js', className: 'TextAnimation' },
  'header-scroll': { file: 'header-scroll.js', className: 'HeaderScroll' },
  'viewport-height-manager': {
    file: 'viewport-height-manager.js',
    className: 'ViewportHeightManager',
  },
  'viewport-min-width': {
    file: 'viewport-min-width.js',
    className: 'ViewportMinWidth',
  },
  'cursor-follower': {
    file: 'cursor-follower.js',
    className: 'CursorFollower',
  },
  'section-divider': {
    file: 'section-divider.js',
    className: 'SectionDivider',
  },
  'scroll-reveal': {
    file: 'scroll-reveal.js',
    className: 'ScrollReveal',
  },
  'line-reveal': {
    file: 'line-reveal.js',
    className: 'LineReveal',
  },
  'image-slide': {
    file: 'image-slide.js',
    className: 'ImageSlide',
  },
  'image-marquee': {
    file: 'image-marquee.js',
    className: 'ImageMarquee',
  },
  'decorative-circles': {
    file: 'decorative-circles.js',
    className: 'DecorativeCircles',
  },
  'copy-link': {
    file: 'copy-link.js',
    className: 'CopyLink',
  },
  'sticky-center': {
    file: 'sticky-center.js',
    className: 'StickyCenter',
  },
  'video-autoplay': {
    file: 'video-autoplay.js',
    className: 'VideoAutoplay',
  },
  'tag-filter': {
    file: 'tag-filter.js',
    className: 'TagFilter',
  },
  'contact-form': {
    file: 'contact-form.js',
    className: 'ContactForm',
  },
  'members-shuffle': {
    file: 'members-shuffle.js',
    className: 'MembersShuffle',
  },
  'member-avatar-svg': {
    file: 'member-avatar-svg.js',
    className: 'MemberAvatarSvg',
  },
  'member-color-swatch': {
    file: 'member-color-swatch.js',
    className: 'MemberColorSwatch',
  },
  'member-tabs': {
    file: 'member-tabs.js',
    className: 'MemberTabs',
  },
  'member-work-shuffle': {
    file: 'member-work-shuffle.js',
    className: 'MemberWorkShuffle',
  },
  'member-reviews-shuffle': {
    file: 'member-reviews-shuffle.js',
    className: 'MemberReviewsShuffle',
  },
  'idle-rakuda': {
    file: 'idle-rakuda.js',
    className: 'IdleRakuda',
  },
  'random-bg-color': {
    file: 'random-bg-color.js',
    className: 'RandomBgColor',
  },
  'phase-stack-fit': {
    file: 'phase-stack-fit.js',
    className: 'PhaseStackFit',
  },
  'hero-heart-random': {
    file: 'hero-heart-random.js',
    className: 'HeroHeartRandom',
  },
};

export class ComponentLoader {
  constructor() {
    /** @type {Map<HTMLElement, Map<string, unknown>>} 要素ごとの component 名 → instance */
    this.components = new Map();
    this.init();
    document.addEventListener('components:refresh', () => this.refresh());
  }

  /**
   * data-component属性を持つ要素を検索し、コンポーネントを初期化。
   * data-component="a b" のように space 区切りで複数 component 名を指定可能 (#569)。
   */
  init() {
    document.querySelectorAll('[data-component]').forEach(element => {
      if (!(element instanceof HTMLElement)) return;
      const componentNames = (element.dataset.component ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      componentNames.forEach(name => {
        if (this.components.get(element)?.has(name)) return;
        this.loadComponent(name, element);
      });
    });
  }

  /**
   * 指定されたコンポーネントを動的に読み込み、初期化
   * @param {string} componentName - コンポーネント名
   * @param {HTMLElement} element - 初期化対象の要素
   * @returns {Promise<void>}
   */
  async loadComponent(componentName, element) {
    if (STATIC_COMPONENTS.has(componentName)) return;

    const config = COMPONENT_MAP[componentName];

    if (!config) {
      console.warn(`Unknown component: ${componentName}`);
      return;
    }

    const modulePath = `../components/${config.file}`;
    const moduleLoader = componentModules[modulePath];

    if (!moduleLoader) {
      console.warn(`Module not found: ${modulePath}`);
      return;
    }

    try {
      const componentModule = await moduleLoader();
      const ComponentClass =
        /** @type {new (element: HTMLElement) => unknown} */ (
          componentModule[config.className]
        );

      if (ComponentClass) {
        const instance = new ComponentClass(element);
        let perElement = this.components.get(element);
        if (!perElement) {
          perElement = new Map();
          this.components.set(element, perElement);
        }
        perElement.set(componentName, instance);

        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log(`✅ ${componentName} component initialized`, {
            element,
            instance,
          });
        }
      }
    } catch (error) {
      console.error(`Failed to load component ${componentName}:`, error);
    }
  }

  /**
   * 新しく追加された要素に対してコンポーネントを初期化
   * 動的にDOM要素が追加された場合に呼び出す
   */
  refresh() {
    this.init();
  }

  /**
   * 特定の要素に紐づく全 component を破棄
   * @param {HTMLElement} element - 破棄対象の要素
   */
  destroy(element) {
    const perElement = this.components.get(element);
    if (!perElement) return;
    perElement.forEach(instance => {
      const obj = /** @type {{ destroy?: () => void } | undefined} */ (
        instance
      );
      obj?.destroy?.();
    });
    this.components.delete(element);
  }
}
